import { useState } from "react";
import { Newspaper, Send, Check, Download } from "lucide-react";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";

const PRESS_KIT_URL = `${process.env.REACT_APP_BACKEND_URL}/api/press-kit.zip`;

/**
 * Press Inquiry form. Routes to the PR Director / Press Secretary inbox
 * (ZaRayiah Porter) via POST /api/press-inquiry. Stores every submission in
 * the admin inbox even if email isn't wired up yet.
 */
export default function PressInquiryForm() {
    const [form, setForm] = useState({
        name: "", email: "", organization: "", phone: "",
        subject: "", deadline: "", message: "",
    });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Name, email, and message are required.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/press-inquiry", {
                ...form,
                subject: form.subject || "Press Inquiry",
            });
            setDone(true);
            toast.success("Inquiry sent to the Press Secretary. We'll be in touch.");
        } catch (err) {
            toast.error(formatErr(err));
        } finally {
            setLoading(false);
        }
    };

    if (done) {
        return (
            <section
                className="border-t border-line bg-bg-soft py-20 md:py-24"
                data-testid="press-inquiry-success"
            >
                <div className="max-w-3xl mx-auto px-5 md:px-10 text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gold/15 border border-gold/40 mb-5">
                        <Check className="text-gold" size={26} />
                    </div>
                    <h2 className="h-display text-4xl md:text-5xl text-white">Inquiry Received</h2>
                    <p className="text-ink text-base mt-4 max-w-lg mx-auto">
                        Thanks — the Press Secretary has your request and will respond within 24 business hours.
                    </p>
                </div>
            </section>
        );
    }

    return (
        <section
            className="border-t border-line bg-bg-soft py-20 md:py-24"
            data-testid="press-inquiry-form"
        >
            <div className="max-w-4xl mx-auto px-5 md:px-10">
                <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-gold/40 text-gold text-[10px] tracking-[0.3em] uppercase font-bold">
                        <Newspaper size={12} /> Media · Press
                    </div>
                    <h2 className="h-display text-4xl md:text-5xl text-white mt-5">Press Inquiry</h2>
                    <p className="text-ink text-base mt-4 max-w-xl mx-auto">
                        Working on a story, interview, or feature? Reach the Purple Kings Press Secretary directly.
                    </p>
                    <div className="mt-6 flex justify-center">
                        <a
                            href={PRESS_KIT_URL}
                            download
                            data-testid="press-kit-download"
                            className="inline-flex items-center gap-2 px-5 py-3 border border-line hover:border-gold text-white text-xs tracking-[0.2em] uppercase font-bold transition-colors"
                        >
                            <Download size={14} className="text-gold" />
                            Download Press Kit (ZIP)
                        </a>
                    </div>
                </div>

                <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
                    <Field label="Your Name *" value={form.name} onChange={onChange("name")} testid="press-name" required />
                    <Field label="Email *" type="email" value={form.email} onChange={onChange("email")} testid="press-email" required />
                    <Field label="Organization / Outlet" value={form.organization} onChange={onChange("organization")} testid="press-org" />
                    <Field label="Phone" value={form.phone} onChange={onChange("phone")} testid="press-phone" />
                    <Field label="Subject" value={form.subject} onChange={onChange("subject")} testid="press-subject" placeholder="e.g. Interview request — Coach Keppard" />
                    <Field label="Deadline" value={form.deadline} onChange={onChange("deadline")} testid="press-deadline" placeholder="e.g. Friday, 5pm CT" />

                    <div className="md:col-span-2">
                        <label className="block text-[10px] tracking-[0.3em] uppercase text-ink font-bold mb-2">
                            Message *
                        </label>
                        <textarea
                            value={form.message}
                            onChange={onChange("message")}
                            data-testid="press-message"
                            required
                            rows={6}
                            placeholder="Brief on the angle, who you'd like to speak with, and the format (phone, in-person, written)."
                            className="w-full px-5 py-4 text-sm bg-bg border border-line focus:border-gold outline-none transition-colors resize-y"
                        />
                    </div>

                    <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                        <p className="text-ink text-xs">
                            Routed to <span className="text-white">ZaRaya Porter</span>, Press Secretary.
                        </p>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="press-submit"
                            className="btn-gold px-7 py-4 text-xs tracking-[0.2em] uppercase font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                        >
                            {loading ? "Sending…" : <>Send Inquiry <Send size={14} /></>}
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
}

function Field({ label, testid, type = "text", required, placeholder, value, onChange }) {
    return (
        <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-ink font-bold mb-2">
                {label}
            </label>
            <input
                type={type}
                required={required}
                placeholder={placeholder}
                value={value}
                onChange={onChange}
                data-testid={testid}
                className="w-full px-5 py-4 text-sm bg-bg border border-line focus:border-gold outline-none transition-colors"
            />
        </div>
    );
}
